import { type CSSProperties } from "react"

import { type StudentArtwork } from "#customTypes/composedTypes/studentTypes"
import { type Uuid } from "#customTypes/globalTypes"

/**
 * One tile on the wall. Surfaces build these from data they already own; the
 * wall never subscribes to Firestore itself.
 */
export interface ArtworkWallItem {
  /** Stable key — normally the artwork id. */
  id: string
  artwork: StudentArtwork
  studentId: Uuid
  studentName?: string
  /** Alt text / accessible name. Falls back to a generic label. */
  caption?: string
  /**
   * Short visible label in the tile's bottom-left badge corner, for surfaces
   * where the tile's role matters more than its image alone (e.g. which
   * competition box an entry occupies). Sits under the version badge when
   * both apply.
   */
  badge?: string
  /**
   * Items sharing a key are versions of one thing — the steps of a portfolio
   * project — and collapse to the newest when `dedupeVersions` is on. Items
   * without a key never dedupe.
   */
  versionGroupKey?: string
  /** Higher wins within a `versionGroupKey`; ties fall back to `timeCreated`. */
  versionOrder?: number
}

export const WALL_LAYOUT = {
  /** Uniform square crops, `grid-template-columns: repeat(n, 1fr)`. */
  SQUARE: "square",
  /** True aspect, `react-masonry-css` columns. */
  FULL: "full",
} as const

export type WallLayout = (typeof WALL_LAYOUT)[keyof typeof WALL_LAYOUT]

export interface ArtworkWallPrefs {
  layout: WallLayout
  /** Index into ZOOM_LEVELS; never the strip. */
  zoomIndex: number
}

export interface ArtworkWallProps {
  items: readonly ArtworkWallItem[]
  /** Collapse `versionGroupKey` groups to their newest item. Default true. */
  dedupeVersions?: boolean
  /** Tiles revealed per sentinel intersection. Default 48. */
  pageSize?: number
  /** Overrides the stored grid zoom on mount. */
  initialZoomIndex?: number
  /** localStorage key for layout + zoom; lets surfaces keep separate prefs. */
  prefsStorageKey?: string
  emptyText?: string
  onTileClick?: (item: ArtworkWallItem, index: number) => void
  onZoomChange?: (zoomIndex: number) => void
  onLayoutChange?: (layout: WallLayout) => void
  onStripEnter?: (item: ArtworkWallItem) => void
  onStripExit?: () => void
  className?: string
  style?: CSSProperties
}
